import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex flex-row justify-between items-center w-full" }
const _hoisted_2 = { class: "flex flex-row gap-1 items-center" }
const _hoisted_3 = { class: "title" }

import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";
import { base64ToFile } from "@/common/utils/files";

import {
  methodItemCreate,
  methodItemDelete,
  methodItemList,
  methodItemUpdate,
  methodItemShow,
  methodItemUpload,
  methodItemModelCSV,
} from "@/common/api/rest/method/method-item";

import { methodFieldList } from "@/common/api/rest/method/method-field";

import type {
  MethodItemType,
  MethodItemPayloadType,
} from "@/common/types/method/method-item";
import type { FileType } from "@/common/types/common";
import type { FieldType } from "@/common/types/field";

import { RegisterTheme } from "@/common/themes";
import {
  baseConfirm,
  baseModal,
  baseTable,
  iconMenu,
  svgIcon,
} from "@/common/components";

import { fileUploadForm, methodItemForm } from "@/components";


export default /*@__PURE__*/_defineComponent({
  __name: 'method',
  setup(__props) {

const { t, tm } = useI18n();
const router = useRouter();
const route = useRoute();

const fieldData = ref<FieldType[]>([]);
const itemsData = ref<MethodItemType[]>([]);

const itemPayload = ref<MethodItemPayloadType | null>(null);

const selectedItem = ref<MethodItemType | null>(null);

const itemId = ref<string | null>(null);

const fileUpload = ref<FileType | null>(null);
const fileExtensions = ref(["csv"]);

const isLoading = ref(false);

const isEdit = ref(false);
const showItemAdd = ref(false);
const showItemDel = ref(false);
const showModalUpload = ref(false);

const itemLimit = ref<number>(10);
const itemPage = ref<number>(0);
const itemPages = ref<number>(0);
const itemSearch = ref<string>("");
const itemSearched = ref<number>(0);
const itemTotal = ref<number>(0);

const accountId = computed(() => String(route.params.id));
const groupId = computed(() => String(route.params.group));

const menuTabs = computed(() => [
  {
    key: "edit",
    icon: "home-line",
    label: t("common.labels.group"),
  },
  {
    key: "field",
    icon: "field",
    label: t("common.labels.fields"),
  },
  {
    key: "method",
    icon: "method",
    label: t("common.labels.methods"),
  },
  {
    key: "question",
    icon: "form",
    label: t("common.labels.questions"),
  },
]);

const itemDelMessage = computed(() => {
  const message = t("method.texts.del_confirm", {
    concat: selectedItem.value?.name,
  });
  const undo = t("common.texts.undo_message");

  return `${message}<br>${undo}`;
});

const tableHeader = computed(() => [
  { key: "code", label: t("common.labels.code") },
  { key: "name", label: t("common.labels.name") },
]);

function back() {
  router.push(`/${accountId.value}/register/method`);
}

function goToTab(tab: string) {
  router.push(`/${accountId.value}/register/method/${groupId.value}/${tab}`);
}

function fetchFields() {
  methodFieldList(accountId.value, groupId.value, {
    page: 0,
    limit: 500,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        fieldData.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchData() {
  methodItemList(accountId.value, groupId.value, {
    limit: itemLimit.value,
    page: itemPage.value,
    search: itemSearch.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        itemPage.value = data.infos.page;
        itemPages.value = data.infos.pages;
        itemTotal.value = data.infos.total;
        itemSearched.value = data.infos.count;
        itemsData.value = [];
        itemsData.value = [...data.list];
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function itemAdd() {
  showItemAdd.value = true;
}

function itemAddCancel() {
  isEdit.value = false;
  showItemAdd.value = false;
  itemPayload.value = null;
  itemId.value = null;
}

function itemAddConfirm() {
  if (!itemPayload.value) return;
  store.commit("SET_LOADING", true);

  methodItemCreate(accountId.value, groupId.value, itemPayload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        itemAddCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function itemEdit(value: MethodItemType) {
  itemId.value = value.method_id;
  isEdit.value = true;

  methodItemShow(accountId.value, value.method_id)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        itemPayload.value = data;
        showItemAdd.value = true;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function itemEditConfirm() {
  if (!itemPayload.value || !itemId.value) return;

  methodItemUpdate(accountId.value, itemId.value, itemPayload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        itemAddCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function itemSubmit() {
  if (!isEdit.value) {
    itemAddConfirm();
  } else {
    itemEditConfirm();
  }
}

function itemDel(data: MethodItemType) {
  selectedItem.value = data;
  showItemDel.value = true;
}

function itemDelCancel() {
  selectedItem.value = null;
  showItemDel.value = false;
}

function itemDelConfirm() {
  if (!selectedItem.value) return;

  showItemDel.value = false;
  store.commit("SET_LOADING", true);

  methodItemDelete(accountId.value, selectedItem.value.method_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        itemDelCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changeItemPage(value: number) {
  itemPage.value = value;
  fetchData();
}

function searchItem(value: string) {
  itemSearch.value = value;
  fetchData();
}

function uploadCsv() {
  showModalUpload.value = true;
}

function uploadCancel() {
  showModalUpload.value = false;
  fileUpload.value = null;
}

function uploadConfirm() {
  if (!fileUpload.value) return;

  showModalUpload.value = false;
  store.commit("SET_LOADING", true);

  methodItemUpload(accountId.value, groupId.value, fileUpload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        uploadCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function downloadModel() {
  methodItemModelCSV(accountId.value, groupId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        base64ToFile(data.file, data.name, data.type);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  if (groupId.value) {
    fetchData();
    fetchFields();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(RegisterTheme), { "tab-active": "method" }, {
      title: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_unref(svgIcon), {
              icon: "method",
              size: "2rem"
            }),
            _createTextVNode(" " + _toDisplayString(_unref(t)("common.labels.edit", { concat: _unref(t)("common.labels.method") })), 1)
          ]),
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-back btn-sm",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (back()))
          }, [
            _createVNode(_unref(svgIcon), {
              icon: "close",
              size: "1.25rem"
            })
          ])
        ])
      ]),
      content: _withCtx(() => [
        _createVNode(_unref(iconMenu), {
          tabs: menuTabs.value,
          active: "method",
          onSelected: _cache[3] || (_cache[3] = ($event: any) => (goToTab($event)))
        }, {
          content: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)("common.labels.methods")), 1),
            _createVNode(_unref(baseTable), {
              header: tableHeader.value,
              data: itemsData.value,
              loading: isLoading.value,
              options: true,
              edit: true,
              del: true,
              paginated: true,
              upload: true,
              limit: itemLimit.value,
              page: itemPage.value,
              pages: itemPages.value,
              total: itemTotal.value,
              searched: itemSearched.value,
              "onChange:page": changeItemPage,
              onSearching: searchItem,
              "onBtn:add": _cache[1] || (_cache[1] = ($event: any) => (itemAdd())),
              "onBtn:edit": itemEdit,
              "onBtn:del": itemDel,
              "onBtn:upload": _cache[2] || (_cache[2] = ($event: any) => (uploadCsv()))
            }, null, 8, ["header", "data", "loading", "limit", "page", "pages", "total", "searched"])
          ]),
          _: 1
        }, 8, ["tabs"])
      ]),
      _: 1
    }),
    _createVNode(_unref(baseModal), {
      width: "80%",
      title: 
      isEdit.value ? _unref(t)('method.texts.edit_method') : _unref(t)('method.texts.add_method')
    ,
      show: showItemAdd.value,
      "show-button-confirm": true,
      "button-confirm": _unref(t)('common.labels.confirm'),
      "button-close": _unref(t)('common.labels.cancel'),
      onClose: _cache[5] || (_cache[5] = ($event: any) => (itemAddCancel())),
      onConfirm: _cache[6] || (_cache[6] = ($event: any) => (itemSubmit()))
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(methodItemForm), {
          width: "100%",
          data: itemPayload.value,
          fields: fieldData.value,
          onUpdateData: _cache[4] || (_cache[4] = ($event: any) => (itemPayload.value = $event))
        }, null, 8, ["data", "fields"])
      ]),
      _: 1
    }, 8, ["title", "show", "button-confirm", "button-close"]),
    _createVNode(_unref(baseModal), {
      show: showModalUpload.value,
      title: 
      _unref(t)('common.labels.upload', {
        concat: _unref(t)('common.labels.method'),
      }).toUpperCase()
    ,
      "show-button-confirm": true,
      "button-confirm": _unref(t)('common.labels.send'),
      "button-close": _unref(t)('common.labels.cancel'),
      onClose: _cache[9] || (_cache[9] = ($event: any) => (uploadCancel())),
      onConfirm: _cache[10] || (_cache[10] = ($event: any) => (uploadConfirm()))
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(fileUploadForm), {
          extensions: fileExtensions.value,
          model: true,
          onUpdateData: _cache[7] || (_cache[7] = ($event: any) => (fileUpload.value = $event)),
          onModelDownload: _cache[8] || (_cache[8] = ($event: any) => (downloadModel()))
        }, null, 8, ["extensions"])
      ]),
      _: 1
    }, 8, ["show", "title", "button-confirm", "button-close"]),
    _createVNode(_unref(baseConfirm), {
      show: showItemDel.value,
      title: _unref(t)('method.texts.del_method'),
      message: itemDelMessage.value,
      onCancel: _cache[11] || (_cache[11] = ($event: any) => (itemDelCancel())),
      onConfirm: _cache[12] || (_cache[12] = ($event: any) => (itemDelConfirm()))
    }, null, 8, ["show", "title", "message"])
  ], 64))
}
}

})